import { Control, Controller } from "react-hook-form";
import { FileText } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { ControlledCheckbox } from "@/components/ui/customs/controlled-checkbox";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { UnifiedInput } from "@/components/ui/customs/unified-input";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { CustomPolicyFormData } from "./custom-policy.form";
import { CustomPolicySectionCard } from "./customPolicySectionCard";
import {
  useIdentityDocumentRequiredOptions,
  useImportLicenseRequiredOptions,
} from "@/hooks/useEnumOptions";
import { useTranslation } from "react-i18next";

type Props = {
  control: Control<CustomPolicyFormData>;
  isSuperAdmin: boolean;
};

export const CustomPolicyDocumentsSection = ({ control, isSuperAdmin }: Props) => {
  const { t } = useTranslation("boCustoms");
  const identityDocumentRequiredOptions = useIdentityDocumentRequiredOptions();
  const importLicenseRequiredOptions = useImportLicenseRequiredOptions();
  return (
    <CustomPolicySectionCard icon={FileText} title={t("policyDocuments.title")}>
      <div className="flex flex-col">
        <div className="flex w-full items-center justify-between">
          <Controller
            control={control}
            name="importLicenseRequired"
            render={({ field }) => (
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={field.value !== "NEVER"}
                  onCheckedChange={(checked) => {
                    field.onChange(checked ? "ALWAYS" : "NEVER");
                  }}
                  disabled={!isSuperAdmin}
                />
                <span className="text-sm">{t("policyDocuments.importLicenseRequiredLabel")}</span>
              </label>
            )}
          />
          <ControlledSelectV2
            control={control}
            name="importLicenseRequired"
            options={importLicenseRequiredOptions}
            isDisabled={!isSuperAdmin}
            triggerClassName="w-56"
            wrapperClassName="w-56"
          />
        </div>

        <Separator className="my-4" />

        <ControlledCheckbox
          control={control}
          name="originCertificateRequired"
          label={t("policyDocuments.originCertificateRequiredLabel")}
          disabled={!isSuperAdmin}
        />

        <Separator className="my-4" />

        <ControlledCheckbox
          control={control}
          name="powerOfAttorneyRequired"
          label={t("policyDocuments.powerOfAttorneyRequiredLabel")}
          disabled={!isSuperAdmin}
        />

        <Separator className="my-4" />

        <div className="flex w-full items-center justify-between">
          <Controller
            control={control}
            name="identityDocumentRequired"
            render={({ field }) => (
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={field.value !== "NEVER"}
                  onCheckedChange={(checked) => {
                    field.onChange(checked ? "ALWAYS" : "NEVER");
                  }}
                  disabled={!isSuperAdmin}
                />
                <span className="text-sm">
                  {t("policyDocuments.identityDocumentRequiredLabel")}
                </span>
              </label>
            )}
          />
          <ControlledSelectV2
            control={control}
            name="identityDocumentRequired"
            options={identityDocumentRequiredOptions}
            isDisabled={!isSuperAdmin}
            triggerClassName="w-56"
            wrapperClassName="w-56"
          />
        </div>

        <Separator className="my-4" />

        <Controller
          control={control}
          name="otherRequiredDocuments"
          render={({ field }) => (
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={field.value.length > 0}
                  onCheckedChange={(checked) => {
                    if (!checked) field.onChange([]);
                  }}
                  disabled={!isSuperAdmin}
                />
                <span className="text-sm">{t("policyDocuments.otherDocumentsLabel")}</span>
              </label>
              <UnifiedInput
                value={field.value.join(", ")}
                onChange={(val) => {
                  const str = String(val ?? "");
                  field.onChange(
                    str
                      ? str
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean)
                      : [],
                  );
                }}
                placeholder={t("policyDocuments.otherDocumentsPlaceholder")}
                disabled={!isSuperAdmin}
                labelStyle="none"
                wrapperClassName="w-64 h-[36px]"
              />
            </div>
          )}
        />

        <Separator className="my-4" />

        <Controller
          control={control}
          name="notes"
          render={({ field }) => (
            <Textarea
              value={field.value ?? ""}
              onChange={(e) => field.onChange(e.target.value || null)}
              placeholder={t("policyDocuments.notesPlaceholder")}
              disabled={!isSuperAdmin}
              className="min-h-[100px]"
            />
          )}
        />
      </div>
    </CustomPolicySectionCard>
  );
};
